/**
 * API Client configuration
 * Provides centralized API configuration with tenant domain management
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://6xa6onlw28.execute-api.sa-east-1.amazonaws.com/dev'

export interface ApiError {
  message: string
  statusCode: number
}

export class ApiClientError extends Error {
  constructor(public statusCode: number, message: string) {
    super(message)
    this.name = 'ApiClientError'
  }
}

/**
 * API Client class with tenant domain management
 */
class ApiClient {
  private tenantDomain: string | null = null

  /**
   * Set the tenant domain for all subsequent API calls
   * Called by TenantProvider after bootstrap completes
   */
  setTenantDomain(domain: string): void {
    this.tenantDomain = domain
  }

  /**
   * Get the current tenant domain
   */
  getTenantDomain(): string {
    return this.tenantDomain || import.meta.env.VITE_TENANT_DOMAIN || 'ds.hyperdrivebot.dev'
  }

  /**
   * Makes an API request with tenant headers
   */
  async request<T>(path: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Domain': this.getTenantDomain(),
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        message: 'An unexpected error occurred',
        statusCode: response.status,
      }))

      throw new ApiClientError(
        response.status,
        errorData.message || `HTTP ${response.status}: ${response.statusText}`
      )
    }

    return response.json()
  }

  /**
   * GET request helper
   */
  async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' })
  }

  /**
   * POST request helper
   */
  async post<T>(path: string, data: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export legacy functions for backwards compatibility
export async function apiRequest<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  return apiClient.request<T>(path, options)
}

export async function apiGet<T>(path: string): Promise<T> {
  return apiClient.get<T>(path)
}

export async function apiPost<T>(path: string, data: unknown): Promise<T> {
  return apiClient.post<T>(path, data)
}
